import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { WinbackCode, WinbackCodeService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

export const CancelComplete = observer((): JSX.Element => {
  const { t } = useTranslation();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const [winback, setWinback] = useState<WinbackCode | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Reactivate';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const resp = await WinbackCodeService.getByFilter({ params: { active: true } });
        if (cancelled) return;
        const list = resp.data?.data ?? [];
        setWinback(list.length ? list[list.length - 1] : null);
      } catch (err) {
        console.warn('[CancelComplete] winback load failed', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return (): void => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className='container py-8 text-center'>
        <p className='text-neutral-600'>{t('common.loading')}</p>
      </div>
    );
  }

  const settingsHref = user?.id ? `/app/parent/${user.id}/settings` : '/app/';
  const creditsHref = (user?.id && winback?.code)
    ? `/app/user/${user.id}/credits?voucher=${winback.code}`
    : '/app/';

  return (
    <div className='container py-8 max-w-2xl mx-auto'>
      <h1 className='text-2xl font-semibold mb-4'>{t('pages.cancelComplete.heading')}</h1>

      {winback ? (
        <>
          <p className='mb-4'>{winback.description}</p>
          <p className='mb-6'>
            {t('pages.cancelComplete.redeemPrefix')} <b>{winback.code}</b>{' '}
            {t('pages.cancelComplete.onYour')}{' '}
            <a id='credits' href={creditsHref} className='underline'>{t('pages.cancelComplete.creditsLink')}</a>{t('pages.cancelComplete.thenUpgrade')}{' '}
            <a id='settings' href={settingsHref} className='underline'>{t('pages.cancelComplete.settingsLink')}</a>{t('pages.cancelComplete.redeemSuffix')}
          </p>

          <div className='bg-sapphire-50 px-5 py-4 rounded mb-8'>
            <h3 className='font-semibold mb-2'>{t('pages.cancelComplete.benefitsTitle')}</h3>
            <ul className='list-disc list-inside space-y-1 text-sm'>
              <li>{t('pages.cancelComplete.benefits.reliableBackup')}</li>
              <li>{t('pages.cancelComplete.benefits.trustedNetwork')}</li>
              <li>{t('pages.cancelComplete.benefits.bookAndPay')}</li>
            </ul>
          </div>

          <div className='flex flex-col sm:flex-row gap-3'>
            <a href={settingsHref} id='no-thanks-to-settings' className='flex-1'>
              <Button buttonType={ButtonType.secondary} className='w-full'>
                {t('pages.cancelComplete.noThanks')}
              </Button>
            </a>
            <a href={creditsHref} id='redeem-code' className='flex-1'>
              <Button buttonType={ButtonType.primary} className='w-full'>
                {t('pages.cancelComplete.redeemNow')}
              </Button>
            </a>
          </div>
        </>
      ) : (
        <>
          <p className='mb-6'>{t('pages.cancelComplete.noOffer')}</p>
          <a href={settingsHref} id='back-to-settings'>
            <Button buttonType={ButtonType.primary}>
              {t('pages.cancelComplete.backToSettings')}
            </Button>
          </a>
        </>
      )}
    </div>
  );
});
